import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Logo } from '../components/ui/Logo';
import { Dropdown } from '../components/ui/Dropdown';
import { Button } from '../components/ui/Button';
import HandCoins from '../components/ui/icons/HandCoins';
import { 
  DollarSign, 
  User, 
  UserPlus,
  UserCheck,
  Globe, 
  FileText, 
  HelpCircle, 
  ExternalLink, 
  Home, 
  Menu, 
  Play,
  BarChart3,
  Shield,
  Coins,
  Percent,
  Settings,
  Database,
  LineChart,
  AlertTriangle,
  ShieldCheck,
  Info,
  Building,
  TrendingUp,
  Zap,
  Rocket,
  HandIcon,
  Target,
  Lock,
  Unlock,
  Check,
  CheckCircle,
  ArrowRight,
  CandlestickChart,
  CircleDollarSign,
  Link
} from 'lucide-react';
import { getAccountDropdownItems } from '../utils/getAccountDropdownItems';
import { useUser } from '../hooks/useUser';
import { useLanguage } from '../hooks/useLanguage';
import { getCryptoIcons, getCurrencyIcon } from '../utils/botIcons';

export const HomePage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useUser();
  const { currentLanguage, switchLanguage } = useLanguage();
  const [forceUpdate, setForceUpdate] = useState(0);

  // Проверка PWA режима
  const isPWA = window.matchMedia('(display-mode: standalone)').matches || 
                (window.navigator as any).standalone === true;

  // Проверка Safe Area (только для iPhone)
  const hasSafeArea = window.matchMedia('(display-mode: standalone)').matches && 
                      getComputedStyle(document.documentElement).getPropertyValue('--safe-area-top') !== '0px';
  const safeAreaTop = getComputedStyle(document.documentElement).getPropertyValue('--safe-area-top');


  // Принудительное обновление отступов при первой загрузке PWA
  useEffect(() => {
    document.fonts.ready.then(() => {
      // Небольшая задержка для плавного обновления отступов
      setTimeout(() => {
        setForceUpdate(prev => prev + 1);
      }, 50);
    });
  }, []);

  const accountItems = getAccountDropdownItems(t, navigate, user?.is_admin || false);

  const languageItems = [
    {
      id: 'eng',
      label: 'English',
      onClick: () => switchLanguage('ENG')
    },
    {
      id: 'rus',
      label: 'Русский',
      onClick: () => switchLanguage('RUS')
    }
  ];

  const pagesItems = [
    { id: 'home', label: t('home'), icon: Home },
    { id: 'instructions', label: t('instructions'), icon: Settings, onClick: () => navigate('/instructions') },
    { id: 'faq', label: t('faq'), icon: HelpCircle, onClick: () => navigate('/faq') },
    { id: 'pricing', label: t('pricing'), icon: CircleDollarSign, onClick: () => navigate('/pricing') },
    { id: 'links', label: t('links'), icon: Link, onClick: () => navigate('/links') }
  ];

  const advantages = [
    { icon: Rocket, text: t('advantage_automation') },
    { icon: TrendingUp, text: t('advantage_any_market') },
    { icon: HandCoins, text: t('advantage_profit_only') },
    { icon: Shield, text: t('advantage_spot_only') },
    { icon: Target, text: t('advantage_drawdown') },
    { icon: Settings, text: t('advantage_simple') },
    { icon: BarChart3, text: t('advantage_algorithm') }
  ];

  const exchangeFeatures = [
    { icon: Percent, text: t('exchange_fees') },
    { icon: ShieldCheck, text: t('exchange_reliability') },
    { icon: Database, text: t('exchange_liquidity') }
  ];

  const bots = [
    { 
      name: 'kas', 
      displayName: 'ForsKasBot', 
      description: 'KAS / USDT'
    },
    { 
      name: 'sol', 
      displayName: 'ForsSOLBot', 
      description: 'SOL / USDT'
    },
    { 
      name: 'bnb_usdt', 
      displayName: 'ForsBNBBot', 
      description: 'BNB / USDT',
      hasUsdc: false
    },
    { 
      name: 'doge_usdt', 
      displayName: 'ForsDOGEBot', 
      description: 'DOGE / USDT',
      hasUsdc: false
    },
    {
      name: 'btc_usdt',
      displayName: 'ForsBTCBot',
      description: 'BTC / USDT или BTC / USDC',
      hasUsdc: true,
    },
    { 
      name: 'eth_usdt', 
      displayName: 'ForsETHBot', 
      description: 'ETH / USDT'
    },
    { 
      name: 'xrp_usdt', 
      displayName: 'ForsXRPBot', 
      description: 'XRP / USDT',
      hasUsdc: false
    }
  ];

  return (
    <div className="min-h-screen bg-background" 
         style={{ paddingTop: (isPWA && hasSafeArea) ? `calc(5rem + ${safeAreaTop})` : '5rem' }}
         key={forceUpdate}>
      {/* Header */}
      <header className="fixed top-0 left-0 w-full z-50 bg-header border-b border-border">
        <div className="flex items-center justify-between w-full h-20 px-4">
          {/* Левая колонка */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-4">
              <Logo color="primary" className="" style={{ color: '#525252', marginTop: '1mm', width: 'clamp(48px, 5.3vw, 64px)', height: 'clamp(48px, 5.3vw, 64px)' }} />
              <div className="flex flex-col justify-center min-w-0">
                <h1 className="text-3xl font-extra-bold neon-glow whitespace-nowrap" style={{ color: '#525252' }}>
                  ForsBot
                </h1>
                <p
                  className="text-textSubtle truncate"
                  style={{ fontSize: 'clamp(8px, 2vw, 16px)' }}
                >
                  {t('main_slogan')}
                </p>
              </div>
            </div>
          </div>
          {/* Правая колонка */}
          <div className="flex items-center space-x-1 flex-shrink-0">
              <Dropdown
                id="account"
                trigger={<User className="w-5 h-5 text-iconPrimary" />}
                items={accountItems}
                align="right"
              />
              <Dropdown
                id="language"
                trigger={<span className="text-sm font-medium text-iconPrimary">{currentLanguage}</span>}
                items={languageItems}
                align="right"
              />
              <Dropdown
                id="pages"
                trigger={<Menu className="w-5 h-5 text-iconPrimary" />}
                items={pagesItems}
                align="right"
              />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="w-full px-4 sm:px-6 lg:px-8 py-8 max-w-6xl mx-auto">
        {/* Hero Section */}
        <section className="text-center mb-8 md:mb-16">
          <h1 className="text-2xl md:text-3xl font-bold text-text mb-2 md:mb-4 font-sans">
            {t('home_title')}
          </h1>
          <p className="text-base md:text-xl text-textSubtle max-w-3xl mx-auto font-sans">
            {t('home_subtitle')}
          </p>
        </section>

        {/* Product Description */}
        <section className="mb-8 md:mb-16">
          <div className="bg-header border border-border rounded-lg p-3 md:p-6">
            <p className="text-base md:text-lg text-text leading-relaxed font-sans">
              {t('product_description')}
            </p>
          </div>
        </section>

        {/* Advantages */}
        <section className="mb-8 md:mb-16">
          <h2 className="text-2xl md:text-3xl font-bold text-text mb-4 md:mb-8 text-center font-sans">
            {t('advantages_title')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6">
            {advantages.map((advantage, index) => (
              <div key={index} className="bg-header border border-border rounded-lg p-3 md:p-6 hover:border-primary/50 transition-colors">
                <div className="flex items-start space-x-2 md:space-x-4">
                  <div className="flex-shrink-0">
                    <advantage.icon className="w-5 h-5 md:w-6 md:h-6 text-primary" />
                  </div>
                  <p className="text-sm md:text-base text-text font-sans">{advantage.text}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Exchange */}
        <section className="mb-8 md:mb-16">
          <h2 className="text-2xl md:text-3xl font-bold text-text mb-4 md:mb-8 text-center font-sans">
            {t('exchange_title')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-6">
            {exchangeFeatures.map((feature, index) => (
              <div key={index} className="bg-header border border-border rounded-lg p-3 md:p-6 text-center">
                <feature.icon className="w-6 h-6 md:w-8 md:h-8 text-primary mx-auto mb-2 md:mb-4" />
                <p className="text-sm md:text-base text-text font-sans">{feature.text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Bots Table */}
        <section className="mb-8 md:mb-16">
          <h2 className="text-2xl md:text-3xl font-bold text-text mb-4 md:mb-8 text-center font-sans">
            {t('bots_title')}
          </h2>
          <div className="bg-header border border-border rounded-lg overflow-x-auto mx-auto max-w-md lg:max-w-2xl">
            <table className="w-full text-sm md:text-base">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-2 py-1 md:px-4 md:py-2 text-center text-text font-bold font-sans">{t('bot_table_header')}</th>
                  <th className="px-2 py-1 md:px-4 md:py-2 text-left md:text-center text-text font-bold font-sans pl-12 md:pl-0">{t('trading_pair_header')}</th>
                </tr>
              </thead>
              <tbody>
                {bots.map((bot, index) => {
                  const baseKey = bot.name.includes('_') ? bot.name.split('_')[0] : bot.name;
                  const isUSDCOnly = bot.name.endsWith('_usdc');
                  const quote = isUSDCOnly ? 'USDC' : 'USDT';
                  return (
                  <tr key={index} className="border-t border-border hover:bg-muted/30 transition-colors">
                    <td className="px-2 py-1 md:px-4 md:py-2 text-center text-text font-sans">
                      <div className="flex items-center justify-center gap-2">
                        <span>{bot.displayName}</span>
                      </div>
                    </td>
                    <td className="px-2 py-1 md:px-4 md:py-2 text-left md:text-center text-text font-sans">
                      <div className="flex items-center justify-start md:justify-center gap-2">
                        {bot.hasUsdc ? (
                          // Для ботов с поддержкой USDC показываем оба варианта
                          <div className="flex flex-col items-start gap-2">
                            {/* Первая строка: первая пара + "или" */}
                            <div className="flex items-center justify-start gap-2 w-full">
                              <div className="flex items-center gap-1 pl-8">
                                <img 
                                  src={getCurrencyIcon(baseKey).src} 
                                  alt="Base currency" 
                                  className="w-4 h-4 md:w-5 md:h-5 lg:w-6 lg:h-6" 
                                  style={getCurrencyIcon(baseKey).style}
                                />
                                <span>{baseKey.toUpperCase()}</span>
                                <span className="text-textSubtle">/</span>
                                <span>USDT</span>
                                <img 
                                  src={getCurrencyIcon('usdt').src} 
                                  alt="USDT" 
                                  className="w-4 h-4 md:w-5 md:h-5 lg:w-6 lg:h-6" 
                                  style={getCurrencyIcon('usdt').style}
                                />
                              </div>
                              <span className="text-textSubtle text-sm md:text-base ml-2">{t('or')}</span>
                            </div>
                            {/* Вторая строка: вторая пара с тем же левым отступом */}
                            <div className="flex items-center justify-start gap-1 w-full pl-8">
                              <img 
                                src={getCurrencyIcon(baseKey).src} 
                                alt="Base currency" 
                                className="w-4 h-4 md:w-5 md:h-5 lg:w-6 lg:h-6" 
                                style={getCurrencyIcon(baseKey).style}
                              />
                              <span>{baseKey.toUpperCase()}</span>
                              <span className="text-textSubtle">/</span>
                              <span>USDC</span>
                              <img 
                                src={getCurrencyIcon('usdc').src} 
                                alt="USDC" 
                                className="w-4 h-4 md:w-5 md:h-5 lg:w-6 lg:h-6" 
                                style={getCurrencyIcon('usdc').style}
                              />
                            </div>
                          </div>
                        ) : (
                          // Для ботов только с USDT
                          <div className="flex flex-col items-start gap-2">
                            <div className="flex items-center justify-start gap-1 w-full pl-8 md:pl-0 md:-ml-3">
                              <img 
                                src={getCurrencyIcon(baseKey).src} 
                                alt="Base currency" 
                                className="w-4 h-4 md:w-5 md:h-5 lg:w-6 lg:h-6" 
                                style={getCurrencyIcon(baseKey).style}
                              />
                              <span>{baseKey.toUpperCase()}</span>
                              <span className="text-textSubtle">/</span>
                              <span>{quote}</span>
                              <img 
                                src={getCurrencyIcon(quote.toLowerCase()).src} 
                                alt={quote} 
                                className="w-4 h-4 md:w-5 md:h-5 lg:w-6 lg:h-6" 
                                style={getCurrencyIcon(quote.toLowerCase()).style}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )})}
              </tbody>
            </table>
          </div>
        </section>

        {/* Risks */}
        <section className="mb-8 md:mb-16">
          <h2 className="text-2xl md:text-3xl font-bold text-text mb-2 md:mb-4 text-center font-sans">
            {t('risk_title')}
          </h2>
          <p className="text-sm md:text-base lg:text-lg text-textSubtle text-center mb-4 md:mb-8 font-sans">
            {t('risk_subtitle')}
          </p>
          <div className="bg-header border border-border rounded-lg p-3 md:p-6">
            <p className="text-sm md:text-base lg:text-lg font-semibold text-text mb-1 font-sans text-left">
              {t('risk_intro')}
            </p>
            <div className="space-y-4 md:space-y-6">
              {/* Скам биржи MEXC */}
              <div className="flex items-start space-x-4">
                <AlertTriangle className="w-5 h-5 md:w-6 md:h-6 text-primary flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-base md:text-lg font-semibold text-text mb-1 font-sans">{t('risk_exchange')}</h3>
                  <p className="text-sm md:text-base text-textSubtle font-sans">{t('risk_exchange_desc')}</p>
                </div>
              </div>
              {/* Взлом аккаунта пользователя */}
              <div className="flex items-start space-x-4">
                <Unlock className="w-5 h-5 md:w-6 md:h-6 text-primary flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-base md:text-lg font-semibold text-text mb-1 font-sans">{t('risk_hack')}</h3>
                  <p className="text-sm md:text-base text-textSubtle font-sans">{t('risk_hack_desc')}</p>
                </div>
              </div>
              {/* Риск, связанный с работой бота */}
              <div className="flex items-start space-x-4">
                <CandlestickChart className="w-5 h-5 md:w-6 md:h-6 text-primary flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h3 className="text-base md:text-lg font-semibold text-text mb-1 font-sans">{t('risk_bot')}</h3>
                  <p className="text-sm md:text-base text-textSubtle mb-3 font-sans">{t('risk_bot_desc')}</p>
                  <div className="bg-muted/30 rounded-lg">
                    <p className="text-sm md:text-base font-semibold text-text mb-2 font-sans">{t('risk_bot_but')}</p>
                    <ul className="space-y-1">
                      <li className="text-sm md:text-base text-text flex items-start space-x-2 font-sans">
                        <Check className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                        <span>{t('risk_bot_strong')}</span>
                      </li>
                      <li className="text-sm md:text-base text-text flex items-start space-x-2 font-sans">
                        <Check className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                        <span>{t('risk_bot_settings')}</span>
                      </li>
                      <li className="text-sm md:text-base text-text flex items-start space-x-2 font-sans">
                        <Check className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                        <span>{t('risk_bot_recovery')}</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* About */}
        <section className="mb-8 md:mb-16">
          <div className="bg-header border border-border rounded-lg p-4 md:p-8 text-center md:text-center">
            <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-text mb-4 font-sans">{t('about_title')}</h2>
            <p className="text-sm md:text-base lg:text-lg text-textSubtle max-w-3xl mx-auto font-sans text-left md:text-center">{t('about_text')}</p>
          </div>
        </section>

        {/* Next Steps */}
        <section className="mb-16 text-center md:text-center">
          <div className="bg-header border border-border rounded-lg p-4 md:p-8">
            <div className="space-y-4">
            <p className="text-sm md:text-base lg:text-lg text-text font-sans text-left md:text-center">
                {t('next_steps_instructions_prefix')}{' '}
              <span className="inline-flex items-baseline cursor-pointer font-bold text-primary hover:underline" onClick={() => navigate('/instructions')}>
                <Settings className="w-4 h-4 md:w-5 md:h-5 mr-1 -mb-0.8" />
                {t('instructions')}
              </span>
                {t('next_steps_instructions_suffix')}
              </p>
              <p className="text-sm md:text-base lg:text-lg text-text font-sans text-left md:text-center">
                {t('next_steps_faq_prefix')}{' '}
                <span className="inline-flex items-baseline cursor-pointer font-bold text-primary hover:underline" onClick={() => navigate('/faq')}>
                  <HelpCircle className="w-4 h-4 md:w-5 md:h-5 mr-1 -mb-0.8" />
                  {t('faq')}
                </span>
                {t('next_steps_faq_suffix')}
              </p>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-header border-t border-border py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-lg text-text mb-2 font-sans">{t('footer_welcome')}</p>
          <p className="text-textSubtle font-sans">{t('footer_slogan')}</p>
        </div>
      </footer>
    </div>
  );
}; 